import React, { useState } from 'react';
import PageBanner from '../components/PageBanner';
import { Check, Heart, Calendar, User, ExternalLink } from 'lucide-react';
import CTASection from '../components/CTASection';

const AMOUNTS = [100, 250, 500, 1000, 2500, 5000];
const YOCO_LINK = 'https://pay.yoco.com/moleboheng-matli-foundation';

const BURSARIES = [
  {
    name: 'The National Student Financial Aid Scheme',
    short: 'NSFAS',
    logo: 'https://customer-assets.emergentagent.com/job_site-architecture-1/artifacts/4omdgekf_NSFAS.png',
    fallbackBg: '#2d2360',
    fit: 'cover',
    description:
      "NSFAS is a bursary scheme that provides poor and working-class South African students with fully subsidised higher education and training. Its vision is to support students from poor and working-class families with financial aid to undertake studies at public universities and TVET colleges.",
    link: 'https://www.nsfas.org.za/content/',
  },
  {
    name: 'Funza Lushaka Bursary',
    short: 'Funza Lushaka',
    logo: 'https://customer-assets.emergentagent.com/job_site-architecture-1/artifacts/kyywyx6a_Funza.png',
    fallbackBg: '#ffffff',
    fit: 'contain',
    description:
      'Funza Lushaka is a four-year programme for a Bachelor of Education (B.Ed.). The bursary supports students who are studying and intending to become teachers, and aims to promote teaching as a profession. It offers a range of majors to which candidates can apply.',
    link: 'https://www.funzalushaka.doe.gov.za/',
  },
  {
    name: 'SITA Bursary',
    short: 'SITA',
    logo: 'https://customer-assets.emergentagent.com/job_site-architecture-1/artifacts/7aw2cmbz_SITA.png',
    fallbackBg: '#ffffff',
    fit: 'contain',
    description:
      'The State Information Technology Agency (SITA) bursary supports students pursuing ICT studies. The bursary aims to bridge the gap within the Information and Communication fraternity and covers various fields including Information Technology, Applied Sciences, Social Studies, and Agriculture.',
    link: 'https://www.sita.co.za/',
  },
  {
    name: 'SETA Bursary',
    short: 'SETA',
    logo: 'https://customer-assets.emergentagent.com/job_site-architecture-1/artifacts/m7hl1k5d_image.png',
    fallbackBg: '#ffffff',
    description:
      'The Sector Education and Training Authorities (SETA) bursary advances student skills development. Government funding is directed to learners performing well academically, with the goal of increasing the number of students entering disciplines where critical skills are in short supply.',
    link: 'https://www.wrseta.org.za/',
  },
  {
    name: 'Sasol Bursary',
    short: 'Sasol',
    logo: 'https://customer-assets.emergentagent.com/job_site-architecture-1/artifacts/ghbjskvg_image.png',
    fallbackBg: '#ffffff',
    description:
      'The Sasol bursary is open to talented individuals who wish to study full-time. It is a broad, inclusive bursary that prioritises fields the country needs most for the future, including Engineering & Technology, Data Science, Agricultural Science, Environmental & Medical Sciences, Construction, Manufacturing and Financial Sciences.',
    link: 'https://www.sasolbursaries.com/',
  },
  {
    name: 'Motsepe Foundation Bursary',
    short: 'Motsepe',
    logo: 'https://customer-assets.emergentagent.com/job_site-architecture-1/artifacts/dg0vl2pu_image.png',
    fallbackBg: '#f4d022',
    description:
      'The Motsepe Foundation aims to improve the lives of unemployed and poverty-stricken people across the country. Over 2,000 students are currently receiving this bursary, which covers tuition, accommodation, living expenses, and books.',
    link: 'https://motsepefoundation.org/',
  },
];

const BursaryCard = ({ b }) => (
  <article className="bg-white rounded-sm overflow-hidden shadow-sm border border-slate-100 card-lift flex flex-col">
    <div
      className="h-56 overflow-hidden grid place-items-center"
      style={{ background: b.fallbackBg }}
    >
      <img
        src={b.logo}
        alt={b.short}
        className="w-full h-full object-cover"
        onError={(e) => {
          e.currentTarget.style.display = 'none';
          e.currentTarget.parentElement.innerHTML += `<span style="color:white;font-family:'Quicksand',sans-serif;font-weight:800;font-size:36px;letter-spacing:2px">${b.short}</span>`;
        }}
      />
    </div>
    <div className="p-7 flex-1 flex flex-col">
      {(b.posted || b.author) && (
        <div className="flex items-center gap-5 text-[13px] font-semibold text-[#F97559] mb-4">
          {b.posted && (
            <span className="inline-flex items-center gap-1.5"><Calendar size={13} /> Posted: {b.posted}</span>
          )}
          {b.author && (
            <span className="inline-flex items-center gap-1.5"><User size={13} /> {b.author}</span>
          )}
        </div>
      )}
      <h3 className="heading-display text-xl md:text-2xl font-bold text-slate-900 leading-snug">
        {b.name}
      </h3>
      <p className="mt-4 text-slate-500 leading-7 text-[14.5px] flex-1">
        {b.description}
      </p>
      <a
        href={b.link}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-6 inline-flex items-center justify-center gap-2 bg-[#2BBECF] hover:bg-[#1ca6b6] text-white font-semibold px-6 py-3 rounded-sm transition-colors self-start"
      >
        Apply Now <ExternalLink size={14} />
      </a>
    </div>
  </article>
);

const Funding = () => {
  const [amount, setAmount] = useState(500);
  const [custom, setCustom] = useState('');

  return (
    <main>
      <PageBanner title="Funding & Donations" crumb="Funding" />

      <section className="py-20 md:py-24 bg-slate-50">
        <div className="max-w-[1200px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-5 gap-10">
          <div className="lg:col-span-2">
            <p className="uppercase text-sm tracking-[0.25em] text-slate-500 mb-3">Support our mission</p>
            <h2 className="heading-display text-3xl md:text-4xl font-bold text-slate-900 leading-tight">Your gift transforms lives across <span className="text-[#F97559]">Africa.</span></h2>
            <p className="mt-5 text-slate-600 leading-8 text-[15px]">Every contribution funds scholarships, books, digital classrooms and mentorship for girls in impoverished communities. Choose an amount or enter your own.</p>
            <ul className="mt-6 space-y-3 text-sm text-slate-700">
              {['R 250 buys school supplies for one girl', 'R 1,000 funds a literacy workshop spot', 'R 5,000 sponsors a term of digital learning'].map((t, i) => (
                <li key={i} className="flex items-start gap-3"><span className="w-6 h-6 rounded-full bg-[#2BBECF] text-white grid place-items-center mt-0.5"><Check size={14} /></span>{t}</li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3 bg-white p-8 md:p-10 rounded-sm shadow-sm">
            <div className="inline-flex items-center gap-2 bg-[#F97559]/10 text-[#F97559] px-4 py-2 rounded-sm text-xs font-bold uppercase tracking-wider mb-6">
              <Heart size={14} /> One-time Donation
            </div>

            <p className="text-sm font-semibold text-slate-700 mb-3">Choose amount (ZAR)</p>
            <div className="grid grid-cols-3 gap-3">
              {AMOUNTS.map((a) => (
                <button type="button" key={a} onClick={() => { setAmount(a); setCustom(''); }}
                  className={`py-3 rounded-sm font-semibold text-sm transition-colors ${!custom && amount === a ? 'bg-[#F97559] text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}>
                  R {a.toLocaleString()}
                </button>
              ))}
            </div>
            <div className="mt-3">
              <input value={custom} onChange={(e) => setCustom(e.target.value.replace(/[^0-9]/g, ''))} placeholder="Or enter custom amount" className="w-full px-4 py-3 border border-slate-200 rounded-sm focus:outline-none focus:border-[#F97559] text-sm" />
            </div>

            <a
              href={YOCO_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-7 w-full bg-[#F97559] hover:bg-[#e65a40] text-white font-semibold py-4 rounded-sm transition-colors flex items-center justify-center gap-2"
            >
              <Heart size={18} /> Donate Now <ExternalLink size={14} />
            </a>
            <p className="mt-3 text-xs text-slate-500 text-center">You will be redirected to our secure Yoco payment page. 100% of your donation supports our programs.</p>
          </div>
        </div>
      </section>

      {/* Bursary institutions */}
      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="text-center mb-12">
            <p className="uppercase text-xs tracking-[0.3em] font-bold text-[#F97559] mb-3">
              External Funding Opportunities
            </p>
            <h2 className="heading-display text-4xl md:text-5xl font-bold text-slate-900">
              Bursaries &amp; Scholarships
            </h2>
            <div className="mx-auto mt-4 flex items-center justify-center gap-1">
              <span className="w-10 h-0.5 bg-[#F97559]" />
              <span className="w-2 h-2 rotate-45 bg-[#F97559]" />
              <span className="w-10 h-0.5 bg-[#F97559]" />
            </div>
            <p className="mt-6 max-w-2xl mx-auto text-slate-600 leading-7 text-[15px]">
              We point learners towards trusted national funding institutions
              that can help them access higher education and training.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
            {BURSARIES.map((b, i) => (
              <BursaryCard key={i} b={b} />
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </main>
  );
};

export default Funding;
